/**
 * Metrics scope filter — pre-expand tokens once, then scan logs in one pass.
 * Departments uses a cached index instead of hundreds of checked tokens.
 */

import {
  ActivityGroupsModel,
  ActivitySkuHierarchyGroup,
  ALL_ACTIVITY_SCOPE_ID,
  checkedIncludesShowAll,
  collectScopeOccurrenceKeys,
  collectSkuOccurrenceKeys,
  DEPARTMENTS_METRIC_SCOPE_ID,
  expandActivityGroupsTokens,
  logHasSkuSelectionIds,
  logMatchesActivityGroupsOccurrenceKeys,
  SHOW_ALL_ACTIVITY_LOGS,
} from 'pages/Logs/activityGroupsModel';
import type { Log } from 'pages/Logs/hooks';
import type { MetricsScopeSelection } from './metricsScopeSelection';

type DepartmentMetricsIndex = {
  scopeOccurrenceKeys: string[],
};

const departmentIndexCache = new WeakMap<ActivityGroupsModel, DepartmentMetricsIndex>();

const walkDepartmentScopes = (
  nodes: ActivitySkuHierarchyGroup[],
  parentPath: string[],
  model: ActivityGroupsModel,
  scopeKeys: string[],
) => {
  (nodes || []).forEach((node) => {
    if (node.isObjectGroup) return;
    const group = model.byId.get(node.id);
    const path = [...parentPath, node.id];
    const scopeChildren = (node.children || []).filter((c) => !c.isObjectGroup);
    if (group?.kind === 'department') {
      scopeKeys.push(...collectScopeOccurrenceKeys(node, parentPath));
    }
    if (scopeChildren.length) {
      walkDepartmentScopes(scopeChildren, path, model, scopeKeys);
    }
  });
};

export const getDepartmentMetricsIndex = (
  model: ActivityGroupsModel,
): DepartmentMetricsIndex => {
  const cached = departmentIndexCache.get(model);
  if (cached) return cached;
  const scopeOccurrenceKeys: string[] = [];
  walkDepartmentScopes(model.skuHierarchy, [], model, scopeOccurrenceKeys);
  const index = {
    scopeOccurrenceKeys: [...new Set(scopeOccurrenceKeys)],
  };
  departmentIndexCache.set(model, index);
  return index;
};

const logMatchesDepartmentMetrics = (
  log: Log,
  model: ActivityGroupsModel,
): boolean => {
  const { scopeOccurrenceKeys } = getDepartmentMetricsIndex(model);
  return logMatchesActivityGroupsOccurrenceKeys(log, scopeOccurrenceKeys, model, 'any');
};

/** Filter permission-clean logs to a metrics scope pick. */
export const filterMetricsScopedLogs = (
  logs: Log[],
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel | null | undefined,
): Log[] => {
  const pool = logs || [];
  if (!pool.length) return [];
  const scopesChecked = selection.scopesChecked || [];
  const skuChecked = selection.skuChecked || [];
  if (!scopesChecked.length && !skuChecked.length) return [];

  if (!model?.groups.length) return pool;

  const scopesShowAll = checkedIncludesShowAll(scopesChecked);
  const skusShowAll = checkedIncludesShowAll(skuChecked);
  if (scopesShowAll && skusShowAll) return pool;

  const usesDepartmentsIndex = scopesChecked.includes(DEPARTMENTS_METRIC_SCOPE_ID);
  const otherScopeTokens = scopesChecked.filter(
    (t) => t !== DEPARTMENTS_METRIC_SCOPE_ID && t !== ALL_ACTIVITY_SCOPE_ID,
  );

  const scopeExpanded = otherScopeTokens.length
    ? expandActivityGroupsTokens(otherScopeTokens, model, skusShowAll ? 'scope' : 'scopeLinked')
    : [];
  const skuExpanded = !skusShowAll && skuChecked.length
    ? expandActivityGroupsTokens(skuChecked, model, 'sku')
    : [];

  return pool.filter((log) => {
    const scopeOk = scopesShowAll
      || (usesDepartmentsIndex && logMatchesDepartmentMetrics(log, model))
      || (scopeExpanded.length > 0
        && logMatchesActivityGroupsOccurrenceKeys(
          log,
          scopeExpanded,
          model,
          skusShowAll ? 'scope' : 'scopeLinked',
        ));
    const skuOk = skusShowAll
      || (skuChecked.length === 0
        ? !logHasSkuSelectionIds(log)
        : (skuExpanded.length > 0
          && logMatchesActivityGroupsOccurrenceKeys(log, skuExpanded, model, 'sku')));
    if (scopesChecked.length && skuChecked.length) {
      return scopeOk && skuOk;
    }
    if (scopesChecked.length) return scopeOk && skuOk;
    return skuOk;
  });
};
